/**
 * Sign-Off Policy Utilities
 * Determines whether a test requires sign-off based on policy settings
 */

/**
 * Sign-off policy modes
 */
export const SIGN_OFF_MODES = {
  STANDARD: 'standard',           // All tests require team lead sign-off
  NO_SIGN_OFF: 'no_sign_off',     // Tests auto-complete without sign-off
  DAILY_AUTO: 'daily_auto',       // Daily tests auto-sign, others require approval
  COMPETENCY_BASED: 'competency_based', // User competency determines requirement
};

/**
 * Competency levels
 */
export const COMPETENCY_LEVELS = {
  TRAINEE: 1,      // All work requires sign-off
  COMPETENT: 2,    // Can self-sign routine tests
  SENIOR: 3,       // Can self-sign all tests
  SUPERVISOR: 4,   // Can sign off others' work
};

/**
 * Get competency level label
 */
export const getCompetencyLabel = (level) => {
  switch (level) {
    case 1: return 'Trainee';
    case 2: return 'Competent';
    case 3: return 'Senior';
    case 4: return 'Supervisor';
    default: return 'Unknown';
  }
};

/**
 * Determine if a test requires sign-off
 * @param {object} options
 * @param {object} options.policy - Sign-off policy from app_settings
 * @param {object} options.user - Current user with competency_level and can_self_sign_off
 * @param {string} options.testFrequency - Test frequency (daily, weekly, monthly, etc.)
 * @param {boolean} options.isRetest - Whether this is a retest after failure
 * @returns {boolean} - True if sign-off is required
 */
export const requiresSignOff = ({ policy, user, testFrequency, isRetest: _isRetest = false }) => {
  // Admins never require sign-off (they can approve their own work)
  if (user?.role === 'admin') {
    return false;
  }

  // Default to requiring sign-off if no policy
  if (!policy) return true;

  const mode = policy.mode || SIGN_OFF_MODES.STANDARD;
  const autoSignTestTypes = policy.autoSignTestTypes || ['daily'];
  const _competencyEnabled = policy.competencyEnabled || false;

  // Mode: No sign-off - everything auto-completes (for all users including admins)
  if (mode === SIGN_OFF_MODES.NO_SIGN_OFF) {
    return false;
  }

  // Mode: Standard - all tests require sign-off (except configured auto-sign types)
  if (mode === SIGN_OFF_MODES.STANDARD) {
    // Check if this test type is in the auto-sign list
    if (autoSignTestTypes.includes(testFrequency?.toLowerCase())) {
      return false;
    }
    return true;
  }

  // Mode: Daily auto - only daily tests auto-sign
  if (mode === SIGN_OFF_MODES.DAILY_AUTO) {
    return testFrequency?.toLowerCase() !== 'daily';
  }

  // Mode: Competency-based
  if (mode === SIGN_OFF_MODES.COMPETENCY_BASED) {
    // User has explicit self-sign permission
    if (user?.canSelfSignOff || user?.can_self_sign_off) {
      return false;
    }

    const userLevel = user?.competencyLevel || user?.competency_level || COMPETENCY_LEVELS.TRAINEE;

    // Trainee - always requires sign-off
    if (userLevel === COMPETENCY_LEVELS.TRAINEE) {
      return true;
    }

    // Competent - can self-sign routine tests (daily, weekly)
    if (userLevel === COMPETENCY_LEVELS.COMPETENT) {
      const routineTests = ['daily', 'weekly'];
      return !routineTests.includes(testFrequency?.toLowerCase());
    }

    // Senior or Supervisor - can self-sign all tests
    if (userLevel >= COMPETENCY_LEVELS.SENIOR) {
      return false;
    }

    return true;
  }

  // Default: require sign-off
  return true;
};

/**
 * Check if user can sign off others' work
 * @param {object} user - User object
 * @returns {boolean}
 */
export const canSignOffOthers = (user) => {
  // Role-based check
  if (user?.role === 'admin' || user?.role === 'team_lead') {
    return true;
  }

  // Competency-based check
  const userLevel = user?.competencyLevel || user?.competency_level || COMPETENCY_LEVELS.TRAINEE;
  return userLevel >= COMPETENCY_LEVELS.SUPERVISOR;
};

/**
 * Get sign-off mode display label
 */
export const getSignOffModeLabel = (mode) => {
  switch (mode) {
    case SIGN_OFF_MODES.STANDARD:
      return 'Standard (All tests require sign-off)';
    case SIGN_OFF_MODES.NO_SIGN_OFF:
      return 'No Sign-Off Required';
    case SIGN_OFF_MODES.DAILY_AUTO:
      return 'Daily Auto-Sign (Others require sign-off)';
    case SIGN_OFF_MODES.COMPETENCY_BASED:
      return 'Competency-Based';
    default:
      return 'Standard';
  }
};
